import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import type { GranulationTypeId } from 'webapp/enums/enums';
import { Scale } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sanitize } from 'webapp/utils/html';
import { isDurationFormat } from 'webapp/utils/number';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import BlockSettingsMetricCompareComponent from 'webapp/components/block/settings/metric/compare';
import BlockSettingsMetricComparesComponent from 'webapp/components/block/settings/metric/compares';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataSelectEditAutoUpdateComponent from 'webapp/components/data-select/edit/auto-update';
import FormErrorComponent from 'webapp/components/form-error';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormCumulativeSwitchComponent from 'webapp/components/form/cumulative-switch';
import FormGranulationDropdownComponent from 'webapp/components/form/granulation/dropdown';
import FormLeaveConfirmPopupComponent from 'webapp/components/form/leave-confirm/popup';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import FormRangeDropdownComponent from 'webapp/components/form/range/dropdown';
import FormSelectComponent from 'webapp/components/form/select';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectFormatComponent from 'webapp/components/form/select-format';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import RestrictedDropdownComponent from 'webapp/components/restricted-dropdown';
import RowErrorComponent from 'webapp/components/row-error';
import ToolTipComponent from 'webapp/components/tool-tip';

import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type PopupModal from 'webapp/models-custom/popup/modal';

interface Args {
	popup: PopupModal; // for discard
	model: DataSelect;
	closeOnSave: boolean;
	isScriptAdvanced?: boolean;
	closeFn: () => void;
	saveFn?: (ds: DataSelect) => void;
	blockSetup?: BlockSetup; // only for designer and reports block edit
}

export default class DataSelectEditFormComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked model!: DataSelect; // not a getter to detect change in models
	@tracked submitInProgress = false;
	@tracked isSaved = false;
	@tracked formatOptions: LabelValuePairString[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updatePropertiesFromModel();
	}

	get blockSetup() {
		return this.args.blockSetup;
	}

	get visualization() {
		return this.args.blockSetup?.visualization;
	}

	get isFromBlock() {
		return !!this.blockSetup;
	}

	get showFilters() {
		return (
			this.showAdvancedFilters || this.args.model.metricSetup.allowBasicDimensionValueFilters
		);
	}

	get showAdvancedFilters() {
		const ds = this.args.model;
		return ds.metricSetup.allowDimensions && ds.allowAdvancedFilters;
	}

	get allowMultipleDimensions() {
		const ds = this.args.model;
		return ds.metricSetup.allowDimensions && ds.allowAdvancedSetup;
	}

	get showFormat() {
		return !this.args.isScriptAdvanced;
	}

	get showScale() {
		if (this.args.isScriptAdvanced) {
			return false;
		} else {
			const format = this.model.overrides.format || this.model.metricFormat || '';
			const isRelativeFormat = format === 'R';
			return !isRelativeFormat && !isDurationFormat(format);
		}
	}

	get selectedScale() {
		const scale = this.model.overrides.scale;
		return typeof scale === 'number' ? scale : Scale.AUTO;
	}

	get scaleOptions() {
		return this.constantsService.scales;
	}

	get showMetricDefinitionEdit() {
		const model = this.model;
		const metric = model.metricSetup.metric;
		return (
			!!metric &&
			!metric.isGeneric &&
			!metric.isTypeGoal &&
			(!metric.isTypeCustomQuery || !metric.source?.isSqlishConnector)
		);
	}

	get showCompares() {
		if (this.args.isScriptAdvanced) {
			return false;
		} else if (this.isFromBlock && this.model.isPrimary) {
			return this.visualization?.showCompareOnPrimaryDs;
		} else {
			return true;
		}
	}

	get dimensionValuesContext() {
		return this.args.isScriptAdvanced ? 'script' : '';
	}

	get isSaveButtonGreen() {
		return this.model.isDirty || this.submitInProgress;
	}

	get isSaveButtonDisabled() {
		return !this.isSaveButtonGreen && !this.isSaved;
	}

	get saveButtonText() {
		return this.isSaveButtonGreen ? 'Save' : this.isSaved ? 'Saved' : 'No changes';
	}

	get saveButtonClass() {
		return this.isSaveButtonGreen ? 'save' : '';
	}

	get saveButtonIcon(): IconName | undefined {
		return this.isSaved && !this.isSaveButtonGreen ? 'Check' : undefined;
	}

	// ---- autoupdate stuff START ----

	get showAutoUpdateInfoForDateRange() {
		return (
			this.isFromBlock &&
			this.model.isPrimary &&
			!!this.visualization?.allowAutoUpdateOnPrimary &&
			this.blockSetup?.firstShownDataSelect?.id !== this.model.id
		);
		// not on the first bcos this is where *block* date-range comes from
	}

	get showAutoUpdateInfoForAllSettings() {
		return this.isFromBlock && !this.model.isPrimary;
	}

	get isAutoUpdateOn() {
		return !this.model.skipAutoUpdate;
	}

	@action
	toggleAutoUpdate() {
		this.model.skipAutoUpdate = !this.model.skipAutoUpdate;
	}

	// ----- autoupdate stuff END ----

	get metricData() {
		return this.model.metricData;
	}

	get singleCompare() {
		// this is for single only (no block)
		return this.model.limitations.compare;
	}

	get allowUnits() {
		return this.model.metricSetup.allowUnitNameFilter;
	}

	get showUnitsAtMetricRow() {
		return this.allowUnits && !this.model.metricSetup.allowDimensions;
	}

	get isMultipleBasicAttributes() {
		return this.showAdvancedFilters || this.model.metricSetup.allowAllDimensionValues;
	}

	@action
	checkIsDirty(): boolean {
		return this.model.isDirty;
	}

	@action
	changeSelectedMetricData(metricData: DSSetup.MetricData): void {
		const dataSelect = this.args.model,
			blockSetup = this.blockSetup;
		if (blockSetup) {
			blockSetup.changeMetric(dataSelect, metricData);
			blockSetup.trackDataSelectMetrickeyChangeEvent(dataSelect);
		} else {
			this.model.updateMetricData(metricData);
		}
		this.updateFormatOptions();
	}

	@action
	changeDimensions(dimensions: string[]): void {
		if (this.blockSetup) {
			this.blockSetup.changeDimensions(this.args.model, dimensions);
		} else {
			this.model.updateDimensions(dimensions);
		}
	}

	@action
	changeFilters(filters: DSSetup.MetricSetupFiltersData | null) {
		if (this.blockSetup) {
			this.blockSetup.changeFilters(this.args.model, filters);
		} else {
			this.model.updateFilters(filters);
		}
	}

	@action
	changeAttributes(attributes: string[]): void {
		const attributesArray = Array.isArray(attributes)
			? attributes
			: attributes
				? [attributes]
				: [];
		const blockSetup = this.blockSetup;
		if (blockSetup) {
			blockSetup.changeAttributes(this.model, attributesArray);
		} else {
			this.model.updateAttributes(attributesArray);
		}
	}

	@action
	changeAttributesSingle(attribute: string | null): void {
		this.changeAttributes(attribute ? [attribute] : []);
	}

	@action
	changeUnit(unit: string | null): void {
		const blockSetup = this.blockSetup;
		if (blockSetup) {
			blockSetup.changeUnit(this.model, unit!);
		} else {
			this.model.updateUnit(unit);
		}
	}

	@action
	changeDateRange(dateRange: DateRange): void {
		const limitations = this.args.model.limitations;
		limitations.updateDateRangeFromOnlyRangeFormula(dateRange.rangeFormula);
		if (!limitations.isNumber) {
			limitations.selectOptimalValidGranulation();
		}
		this.model.updateDateRange(limitations.dateRange!);
	}

	@action
	changeGranulation(granulationId: string | null): void {
		const dateRange = this.model.limitations.dateRange;
		if (dateRange) {
			dateRange.updateGranulationFromTypeId(granulationId as GranulationTypeId);
			this.model.updateDateRange(dateRange);
		}
	}

	@action
	changeCumulative(cumulative: boolean): void {
		const dateRange = this.model.limitations.dateRange;
		if (dateRange) {
			dateRange.granulationCumulative = cumulative;
			this.model.updateDateRange(dateRange);
		}
	}

	@action
	changeFormat(format: string | null): void {
		const formatForOverride = format === this.model.defaultFormat ? undefined : format;
		this.model.setOverride('format', formatForOverride!);
		this.updateFormatOptions();
	}

	@action
	changeScale(scale: number): void {
		this.model.setOverride('scale', scale === Scale.AUTO ? undefined : scale);
	}

	@action
	getFiltersTooltipText() {
		let text = '';

		const metricSetup = this.model.metricSetup;

		const dimensionItems = metricSetup.metricDimensions;
		const getDimensionNameForDimension = (dimension: string) => {
			return dimensionItems.find((d) => d.key === dimension)?.value || dimension;
		};

		const dimensionFilterGroup = metricSetup.dimensionFilterGroup;
		if (dimensionFilterGroup) {
			if (text) {
				text += '<br>';
			}
			text += dimensionFilterGroup.getFilterItemsText(getDimensionNameForDimension);
		}

		return Promise.resolve(sanitize(text));
	}

	@action
	openFiltersPopup() {
		this.mmMetricsService.openFiltersPopup(
			{
				metricSetup: this.args.model.metricSetup,
				apply: (
					_dimensions: string[] | undefined,
					filters: DSSetup.MetricSetupFiltersData | null,
				) => {
					// ignoring dimensions bcos from block settings we only show filters
					// dimensions are only used in master filter popup
					this.changeFilters(filters);
				},
			},
			this.args.popup.theme,
		);
	}

	@action
	openMetricDefinitionPopup() {
		if (!this.showMetricDefinitionEdit) {
			return;
		}

		const metric = this.model.metricSetup.metric;
		if (metric) {
			this.mmMetricsService.editMetric(metric, '', undefined, {
				isOpenedFromQueryOrScriptForm: this.args.isScriptAdvanced,
			});
		}
	}

	@action
	changeIsScriptStatic(isScriptStatic: boolean) {
		this.model.isScriptStatic = isScriptStatic;
	}

	@action
	removeCompare(compare: DsCompare): void {
		// this is for single only (no block)
		this.model.removeCompare(compare);
	}

	@action
	changeCompare(oldCompare: DsCompare, newCompare: DsCompare): void {
		// this is for single only (no block)
		this.model.changeCompare(oldCompare, newCompare);
	}

	@action
	onModelChange() {
		const oldModel = this.model,
			newModel = this.args.model;
		if (newModel.id !== oldModel.id) {
			this.updatePropertiesFromModel();
		}
	}

	@action
	save() {
		if (this.submitInProgress) {
			return;
		}

		if (!this.model.validate()) {
			return;
		}

		const model = this.model;

		if (this.args.saveFn) {
			this.args.saveFn(model);
			this.args.closeFn();
			return;
		}

		this.submitInProgress = true;
		this.analyticsService.event(TrackingEvent.DESIGNER_ADVANCED_POPUP_EDIT, {
			origin: this.model.block?.trackingOrigin,
		});

		// updated date range on main DS that we have a block for
		// will need to call batch daterange update on whole block
		const mustUpdateBatchDateRange =
			!!this.blockSetup &&
			this.model.isPrimary &&
			!this.model.skipAutoUpdate &&
			!model.limitations.dateRange?.isEqual(model.model.dateRange);

		const closeOnSave = this.args.closeOnSave;

		model
			.save()
			.then(() => {
				this.isSaved = true;

				if (this.blockSetup && model.blockId) {
					this.mmBlocksService.addBlockIdToRequestedSamples(String(model.blockId));
				}

				if (closeOnSave) {
					this.args.closeFn();
				}
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showGenericValidationError.bind(this.requestService));

		if (mustUpdateBatchDateRange) {
			void this.blockSetup?.updateDataSelectsFromPrimaryDateRange(
				model.limitations.dateRange!,
			);
		}
	}

	private updatePropertiesFromModel() {
		const model = this.args.model;
		this.model = model;
		this.isSaved = false;
		this.updateFormatOptions();
	}

	private updateFormatOptions(): void {
		const formatOptions: LabelValuePairString[] = Array.from(
				this.constantsService.formatOptions,
			),
			selectedFormat = this.model.format;
		if (selectedFormat && !formatOptions.find((item) => item.value === selectedFormat)) {
			formatOptions.push({
				label: selectedFormat,
				value: selectedFormat,
			});
		}
		this.formatOptions = formatOptions;
	}
	<template>
		<div class="edit-form" {{didUpdate this.onModelChange @model}}>
			<FormErrorComponent @errors={{this.model.errorsArray}} />

			<div class="row">

				{{#if this.model.hasNoMetricPermission}}

					<RestrictedDropdownComponent @name="metrickey" @extraClass="item size-0" />

				{{else}}

					<div class="item size-0 prevLine">

						<FormMetricSelectComponent
							@selected={{this.metricData}}
							@onChange={{this.changeSelectedMetricData}}
							@isDisabled={{this.submitInProgress}}
							@origin="designer"
							@originForMetricFavorite="designer_dropdown"
							@placeholder="Select a metric"
						/>
						<RowErrorComponent @items={{this.model.errors.metricSetupKey}} />

					</div>

				{{/if}}

				{{#if this.showUnitsAtMetricRow}}
					<div class="item size-3 prevLine">
						<FormSelectUnitsComponent
							@selected={{this.model.metricSetup.unitName}}
							@metricSetupForQuery={{@model.metricSetup}}
							@dateRangeForQuery={{this.model.dateRangeCM}}
							@allowAllUnits={{this.model.metricSetup.allowAllUnits}}
							@onChange={{this.changeUnit}}
							@isMultiple={{false}}
							@context={{this.dimensionValuesContext}}
						/>
					</div>
				{{/if}}

			</div>

			{{#if this.model.metricSetup.allowDimensions}}

				<div class="row">

					<div class="item size-2 prevLine">
						<FormSelectDimensionsComponent
							@metricSetup={{@model.metricSetup}}
							@onChange={{this.changeDimensions}}
							@isMultiple={{this.allowMultipleDimensions}}
						/>
					</div>

					{{#if this.showFilters}}
						<div
							class={{joinClasses
								(array
									"item"
									"size-2"
									"prevLine"
									(if this.showAdvancedFilters "action--filters")
								)
							}}
						>
							{{#if this.showAdvancedFilters}}
								<a class="filter-icon" {{on "click" this.openFiltersPopup}}>
									<CommonUiIconComponent @name="Filter" @size="sm" />
									{{#if @model.metricSetup.dimensionFilterItemsCount}}
										<span class="badge blue"></span>
										<ToolTipComponent
											@onShowGetText={{this.getFiltersTooltipText}}
											@maxWidth={{230}}
											@extraClass="master-filter__tooltip"
										/>
									{{/if}}
								</a>
							{{else}}
								{{#if this.isMultipleBasicAttributes}}
									<FormSelectAttributesComponent
										@selected={{this.model.metricSetup.attributeNamesFromBasicFilters}}
										@metricSetupForQuery={{@model.metricSetup}}
										@dateRangeForQuery={{this.model.dateRangeCM}}
										@onChange={{this.changeAttributes}}
										@isMultiple={{true}}
										@maxSelectionLength={{10}}
										@context={{this.dimensionValuesContext}}
									/>
								{{else}}
									<FormSelectAttributesComponent
										@selected={{this.model.metricSetup.firstAttributeNameFromBasicFilters}}
										@metricSetupForQuery={{@model.metricSetup}}
										@dateRangeForQuery={{this.model.dateRangeCM}}
										@onChange={{this.changeAttributesSingle}}
										@isMultiple={{false}}
										@maxSelectionLength={{1}}
										@context={{this.dimensionValuesContext}}
									/>
								{{/if}}
							{{/if}}
						</div>
					{{/if}}

					{{#if this.allowUnits}}
						{{#unless this.showUnitsAtMetricRow}}
							<div class="item size-3 prevLine">
								<FormSelectUnitsComponent
									@selected={{this.model.metricSetup.unitName}}
									@metricSetupForQuery={{@model.metricSetup}}
									@dateRangeForQuery={{this.model.dateRangeCM}}
									@allowAllUnits={{this.model.metricSetup.allowAllUnits}}
									@onChange={{this.changeUnit}}
									@isMultiple={{false}}
									@context={{this.dimensionValuesContext}}
								/>
							</div>
						{{/unless}}
					{{/if}}

				</div>
			{{/if}}

			<div class="row row--date-range">
				<div class="item size-1">
					<FormRangeDropdownComponent
						@metricLimitations={{this.model.limitations}}
						@isDisabled={{this.submitInProgress}}
						@onChange={{this.changeDateRange}}
					/>
				</div>
				{{#unless this.model.limitations.isNumber}}
					<div class="item size-3 cumulativeCt">
						<FormCumulativeSwitchComponent
							@metricLimitations={{this.model.limitations}}
							@onChange={{this.changeCumulative}}
						/>
					</div>
				{{/unless}}

				{{#if this.showAutoUpdateInfoForDateRange}}
					<DataSelectEditAutoUpdateComponent
						@isOn={{this.isAutoUpdateOn}}
						@toggle={{this.toggleAutoUpdate}}
						@isForPrimary={{true}}
					/>
				{{/if}}
			</div>

			<div class="row">

				{{#unless this.model.limitations.isNumber}}
					<div class="item size-3 prevLine">
						<FormGranulationDropdownComponent
							@metricLimitations={{this.model.limitations}}
							@onChange={{this.changeGranulation}}
							@isDisabled={{this.submitInProgress}}
						/>
						<RowErrorComponent @items={{this.model.errors.dateRange}} />
					</div>
				{{/unless}}

				{{#if this.showFormat}}
					<div class="item size-2">
						<FormSelectFormatComponent
							name="format"
							@selected={{this.model.format}}
							@options={{this.formatOptions}}
							@onChange={{this.changeFormat}}
							@onAdd={{this.changeFormat}}
							@isDisabled={{this.submitInProgress}}
						/>
					</div>
				{{/if}}

				{{#if this.showScale}}
					<div class="item size-3">
						<FormSelectComponent
							name="scale"
							@selected={{this.selectedScale}}
							@options={{this.scaleOptions}}
							@onChange={{this.changeScale}}
							@isDisabled={{this.submitInProgress}}
							@placeholder="Scale"
							@tooltipOnSelectionFromProperty=""
							@isRequired={{true}}
							@isDropdownWider={{true}}
							@extraClass="dropdown-scale"
						/>
					</div>
				{{/if}}

				{{#if this.showMetricDefinitionEdit}}
					<div class="item size-1">
						<p class="configure">
							Where are my aggregation settings?
							<br />
							<a {{on "click" this.openMetricDefinitionPopup}}>
								Configure metric settings
								<CommonUiIconComponent @name="External" @size="sm" />
							</a>
						</p>
					</div>
				{{/if}}
			</div>

			{{#if @isScriptAdvanced}}
				<div class="row">
					<FormCheckboxComponent
						@name="scriptstatic"
						@selected={{this.model.isScriptStatic}}
						@label="Retain metric settings for all date ranges"
						@onChange={{this.changeIsScriptStatic}}
					/>
				</div>
			{{/if}}

			{{#if this.showCompares}}

				<div class="compares-line">
					<span>Compares</span>
				</div>

				{{#if this.isFromBlock}}
					{{#if @blockSetup}}
						<BlockSettingsMetricComparesComponent
							@blockSetup={{@blockSetup}}
							@dataSelect={{this.model}}
							@hideLabel={{true}}
							@showFormatAndFunction={{true}}
							@isDisabled={{this.submitInProgress}}
						/>
					{{/if}}
				{{else if this.singleCompare}}
					<div class="bs__metric__compares">
						<BlockSettingsMetricCompareComponent
							@model={{this.singleCompare}}
							@dataSelect={{this.model}}
							@remove={{this.removeCompare}}
							@onChange={{this.changeCompare}}
							@showFormatAndFunction={{true}}
							@isDisabled={{this.submitInProgress}}
						/>
					</div>
				{{/if}}

			{{/if}}

			{{#if this.showAutoUpdateInfoForAllSettings}}
				<DataSelectEditAutoUpdateComponent
					@isOn={{this.isAutoUpdateOn}}
					@toggle={{this.toggleAutoUpdate}}
					@isForPrimary={{false}}
				/>
			{{/if}}

			<div class="controls2">
				<div class="controls2__left">
					<FormLeaveConfirmPopupComponent
						@popup={{@popup}}
						@save={{this.save}}
						@checkIsDirty={{this.checkIsDirty}}
						as |controls|
					>
						<controls.save
							@text={{this.saveButtonText}}
							@extraClass={{this.saveButtonClass}}
							@icon={{this.saveButtonIcon}}
							@isDisabled={{this.isSaveButtonDisabled}}
							@submitInProgress={{this.submitInProgress}}
						/>
					</FormLeaveConfirmPopupComponent>
				</div>
			</div>

		</div>
	</template>
}
